import React from 'react';
import { sanitizeErrorMessage } from '../utils/security';
import { APP_SECURITY } from '../config/security';

/**
 * Secure Error Boundary Component
 * Catches JavaScript errors and prevents sensitive information disclosure
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false, 
      error: null,
      errorInfo: null,
      errorId: null
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Generate unique error ID for tracking
    const errorId = `err_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    
    // Sanitize error message to prevent information disclosure
    const sanitizedError = sanitizeErrorMessage(error);
    
    // Log error securely
    if (APP_SECURITY.FEATURES.ENABLE_DEBUG_LOGS) {
      console.group(`🚨 Error Boundary Caught Error [${errorId}]`);
      console.error('Sanitized Error:', sanitizedError);
      console.error('Component Stack:', errorInfo.componentStack);
      console.groupEnd();
    }

    // Update state with sanitized information
    this.setState({
      hasError: true,
      error: sanitizedError,
      errorInfo: errorInfo,
      errorId: errorId
    });

    // Report to error tracking service (if enabled)
    if (APP_SECURITY.ERROR_REPORTING.ENABLE_SENTRY && window.Sentry) {
      window.Sentry.withScope((scope) => {
        scope.setTag('errorBoundary', true);
        scope.setTag('errorId', errorId);
        scope.setContext('errorInfo', {
          componentStack: errorInfo.componentStack
        });
        window.Sentry.captureException(error);
      });
    }

    // Custom error reporting callback
    if (this.props.onError) {
      this.props.onError(sanitizedError, errorInfo, errorId);
    }
  }

  handleRetry = () => {
    // Clear error state to retry rendering
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
      errorId: null
    });
  };

  handleReload = () => {
    // Reload the page as last resort
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback UI provided by parent
      if (this.props.fallback) {
        return this.props.fallback(
          this.state.error, 
          this.handleRetry, 
          this.state.errorId
        );
      }

      // Default secure error UI
      return (
        <div style={{
          padding: '20px',
          margin: '20px',
          border: '2px solid #ff4444',
          borderRadius: '8px',
          backgroundColor: '#fff5f5',
          color: '#cc0000',
          fontFamily: 'Arial, sans-serif'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: '16px' }}>
            <span style={{ fontSize: '24px', marginRight: '8px' }}>⚠️</span>
            <h3 style={{ margin: 0, color: '#cc0000' }}>Something went wrong</h3>
          </div>
          
          <p style={{ marginBottom: '16px', lineHeight: '1.5' }}>
            We encountered an unexpected error. Your data is safe, but this component 
            couldn't load properly.
          </p>

          {APP_SECURITY.FEATURES.ENABLE_DEBUG_LOGS && this.state.errorId && (
            <p style={{ 
              fontSize: '12px', 
              color: '#666', 
              fontFamily: 'monospace',
              marginBottom: '16px' 
            }}>
              Error ID: {this.state.errorId}
            </p>
          )}

          <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
            <button
              onClick={this.handleRetry}
              style={{
                padding: '8px 16px',
                backgroundColor: '#007bff',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Try Again
            </button>
            
            <button
              onClick={this.handleReload}
              style={{
                padding: '8px 16px',
                backgroundColor: '#6c757d',
                color: 'white',
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontSize: '14px'
              }}
            >
              Reload Page
            </button>

            {this.props.onReport && (
              <button
                onClick={() => this.props.onReport(this.state.errorId)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '4px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Report Issue
              </button>
            )}
          </div>

          {this.props.showDetails && APP_SECURITY.FEATURES.ENABLE_DEBUG_LOGS && (
            <details style={{ marginTop: '16px' }}>
              <summary style={{ cursor: 'pointer', color: '#666' }}>
                Technical Details (Debug Mode)
              </summary>
              <pre style={{
                marginTop: '8px',
                padding: '12px',
                backgroundColor: '#f8f9fa',
                border: '1px solid #dee2e6',
                borderRadius: '4px',
                fontSize: '12px',
                overflow: 'auto',
                maxHeight: '200px'
              }}>
                {this.state.error}
              </pre>
            </details>
          )}
        </div>
      );
    }

    // No error, render children normally
    return this.props.children;
  }
}

/**
 * Higher-Order Component to wrap components with error boundary
 */
export const withErrorBoundary = (WrappedComponent, errorBoundaryProps = {}) => {
  const WithErrorBoundaryComponent = (props) => (
    <ErrorBoundary {...errorBoundaryProps}>
      <WrappedComponent {...props} />
    </ErrorBoundary>
  );

  WithErrorBoundaryComponent.displayName = 
    `withErrorBoundary(${WrappedComponent.displayName || WrappedComponent.name})`;

  return WithErrorBoundaryComponent;
};

/**
 * Hook for functional components to handle errors
 */
export const useErrorHandler = () => {
  const [error, setError] = React.useState(null);

  const handleError = React.useCallback((error) => {
    const sanitizedError = sanitizeErrorMessage(error);
    setError(sanitizedError);
    
    // Log error securely
    if (APP_SECURITY.FEATURES.ENABLE_DEBUG_LOGS) {
      console.error('🚨 Component Error:', sanitizedError);
    }
  }, []);

  const clearError = React.useCallback(() => {
    setError(null);
  }, []);

  // Throw error to be caught by Error Boundary
  if (error) {
    throw new Error(error);
  }

  return { handleError, clearError };
};

export default ErrorBoundary;
